<template>
    <div class="mui-figure-item" :data-testid="`slice-numbers-key-figures-item--${index}`">
        <div
            ref="counterElement"
            class="mui-figure-item__figure"
            :class="{'anim__fade-up': isVisible}"
            :data-testid="`slice-numbers-key-figures-item-figure--${index}`"
        >
            <span>{{ item.figurePrefix }}</span>
            <div>{{ counter }}</div>
            <span>{{ item.figureSuffix }}</span>
        </div>
        <div
            v-if="item.text"
            class="mui-figure-item__caption"
            :class="{'anim__fade-up': isVisible}"
            :data-testid="`slice-numbers-key-figures-item-caption--${index}`"
        >
            {{ item.text }}
        </div>
    </div>
</template>

<script setup lang="ts">
    import {ref, useTemplateRef} from 'vue';
    import {useIntersectionObserver} from '@malt/utils';
    import {useCounterAnimation} from '../../../composables/useCounterAnimation';

    type KeyFigureItem = {
        figure?: number;
        figurePrefix?: string;
        figureSuffix?: string;
        text?: string;
    };

    const isVisible = ref(false);
    const {item, index, thousandsSeparator, decimalSeparator} = defineProps<{
        item: KeyFigureItem;
        index: number;
        thousandsSeparator?: string | undefined;
        decimalSeparator?: string | undefined;
    }>();
    // counter animation
    const counterElement = useTemplateRef('counterElement');
    const targetValue = item.figure as number;

    const {counter, startAnimation} = useCounterAnimation(0, targetValue, {
        thousandsSeparator,
        decimalSeparator,
    });

    // interection observer
    function onIntersection(entries: IntersectionObserverEntry[]) {
        if (entries[0]?.isIntersecting) {
            isVisible.value = true;
            startAnimation();
            stopObserving();
        }
    }
    const {stopObserving} = useIntersectionObserver(counterElement, onIntersection, {options: {threshold: 1}});
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .mui-figure-item {
        text-align: center;

        &__figure {
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: var(--joy-font-family-title);
            letter-spacing: var(--joy-font-letter-spacing-tight);
            color: var(--mui-color-text);
            white-space: nowrap;
            line-height: var(--joy-line-height-small);
            opacity: 0;
            font-size: var(--joy-font-size-secondary-200);

            @include mq.screen_md(min) {
                font-size: var(--joy-font-size-secondary-400);
            }

            span {
                flex: none;
                width: var(--joy-core-spacing-6);
                position: relative;
            }

            &:first-child {
                text-align: right;
            }
        }

        &__caption {
            margin-top: var(--joy-core-spacing-1);
            font-family: var(--joy-font-family-title);
            font-size: var(--joy-font-size-primary-400);
            line-height: var(--joy-line-height-large);
            opacity: 0;
            animation-delay: 0.25s;
        }
    }

    @keyframes fade-up {
        0% {
            opacity: 0;
            transform: translate3d(0, 0.5rem, 0);
        }

        100% {
            opacity: 1;
            transform: translate3d(0, 0, 0);
        }
    }

    .anim__fade-up {
        animation-duration: 0.3s;
        animation-timing-function: ease-out;
        animation-name: fade-up;
        animation-fill-mode: forwards;
    }
</style>
